import {
    ExtendedApiObject,
    ExtendedAreaModels,
    filterConstructionNumbers,
    filterDistricts,
    filterProjectPhases,
    filterPropertyTypes,
    findExtendedObjectById,
} from '@bpd-library/utilities';
import { FilterInput, FilterState, SelectedFilter } from '@source/store/filters';

import {
    HouseSelectorModel,
    HouseSelectorObject,
    HouseSelectorObjectTypeOrder,
    HouseSelectorPolygon,
} from '../components/house-selector/utilities/types';
import { getFilteredData, getSoldProjectsData, propertiesStore, View, ViewType } from '../store';

const FIRST_POLYGON_ORDER = 0;
const LAST_POLYGON_ORDER = 6;

const PROPERTY_TYPE_VIEW_POLYGON_ORDER: HouseSelectorObjectTypeOrder = {
    viewLink: FIRST_POLYGON_ORDER,
    propertyType: FIRST_POLYGON_ORDER,
    apartmentBuilding: FIRST_POLYGON_ORDER,
    projectPhase: FIRST_POLYGON_ORDER + 1,
    district: FIRST_POLYGON_ORDER + 2,
    areaProject: FIRST_POLYGON_ORDER + 3,
    constructionNumber: FIRST_POLYGON_ORDER + 4,
};

const CONSTRUCTION_NUMBER_VIEW_POLYGON_ORDER: HouseSelectorObjectTypeOrder = {
    viewLink: FIRST_POLYGON_ORDER,
    constructionNumber: FIRST_POLYGON_ORDER,
    propertyType: FIRST_POLYGON_ORDER + 1,
    apartmentBuilding: FIRST_POLYGON_ORDER + 1,
    projectPhase: FIRST_POLYGON_ORDER + 2,
    district: FIRST_POLYGON_ORDER + 3,
    areaProject: FIRST_POLYGON_ORDER + 4,
};

const SOLD_DATA_POLYGON_ORDER: HouseSelectorObjectTypeOrder = {
    viewLink: FIRST_POLYGON_ORDER,
    constructionNumber: LAST_POLYGON_ORDER,
    propertyType: LAST_POLYGON_ORDER - 1,
    apartmentBuilding: LAST_POLYGON_ORDER - 1,
    projectPhase: LAST_POLYGON_ORDER - 2,
    district: LAST_POLYGON_ORDER - 3,
    areaProject: LAST_POLYGON_ORDER - 3,
};

function filterDataByDistrict(data: ExtendedAreaModels) {
    const stagedData: ExtendedAreaModels = { ...data };
    const { districtId } = window.__ENVIRONMENT_DETAILS__;
    const isDistrictPage = !!districtId;

    if (isDistrictPage) {
        stagedData.constructionNumbers = filterConstructionNumbers(data.constructionNumbers, {
            districtId,
        });
        stagedData.propertyTypes = filterPropertyTypes(data.propertyTypes, {
            districtId,
        });
        stagedData.projectPhases = filterProjectPhases(data.projectPhases, {
            districtId,
        });
        stagedData.apartmentBuildings = data.apartmentBuildings.filter(
            ({ constructionNumbers = [], propertyTypes = [] }) => {
                return (
                    !!filterConstructionNumbers(constructionNumbers, { districtId }).length ||
                    !!filterConstructionNumbers(propertyTypes, { districtId }).length
                );
            },
        );
        stagedData.districts = filterDistricts(data.districts, { id: districtId });
    }

    return stagedData;
}

function filterHouseSelectorData(
    houseSelectorData: HouseSelectorModel,
    areaData: ExtendedAreaModels,
) {
    houseSelectorData.views?.forEach((view) => {
        view.polygons = view.polygons?.filter(({ objectType, objectId }) => {
            if (objectType === 'viewLink' || !objectId) return true;
            else return findExtendedObjectById(areaData, objectId);
        });
    });
}

function groupFilter(filter: SelectedFilter) {
    const { inputs } = filter!;

    const inputsPerFilter: {
        [key: string]: FilterInput[];
    } = {};

    inputs.forEach((input) => {
        if (!input.checked || !input.name) return;

        if (!inputsPerFilter[input.name]) {
            inputsPerFilter[input.name] = [];
        }

        inputsPerFilter[input.name].push(input);
    });

    return inputsPerFilter;
}

function filterData(data: ExtendedAreaModels, filters: FilterState) {
    const includedIds: { [id: string]: boolean } = {};

    let filteredData: ExtendedAreaModels = { ...data };

    const hasIncludedObject = (objects: { id: string }[]): boolean =>
        !!objects.find(({ id }) => includedIds[id]);

    const objectIsIncluded = (object: { id: string }): boolean => includedIds[object.id];

    Object.keys(filters).forEach((filterName) => {
        const { filterFunctions, filterLogic } = filters[filterName]!;
        const inputsPerFilter = groupFilter(filters[filterName]!);

        Object.keys(inputsPerFilter).forEach((key) => {
            const inputs = inputsPerFilter[key];

            filteredData.constructionNumbers.forEach((constructionNumber) => {
                let isIncluded: boolean;

                if (filterLogic !== '&&') {
                    isIncluded = !!inputs.find((input) =>
                        filterFunctions.constructionNumber(constructionNumber, input),
                    );
                } else {
                    isIncluded = !!inputs.every((input) =>
                        filterFunctions.constructionNumber(constructionNumber, input),
                    );
                }

                includedIds[constructionNumber.id] = isIncluded;
            });

            filteredData.propertyTypes.forEach((propertyType) => {
                const { id, constructionNumbers } = propertyType;

                const hasConstructionNumbers = !!constructionNumbers?.length;

                if (!hasConstructionNumbers) {
                    // If there are no construction numbers, filter based on propertyType data
                    let isIncluded: boolean;

                    if (filterLogic !== '&&') {
                        isIncluded = !!inputs.find((input) =>
                            filterFunctions.propertyType(propertyType, input),
                        );
                    } else {
                        isIncluded = !!inputs.every((input) =>
                            filterFunctions.propertyType(propertyType, input),
                        );
                    }

                    includedIds[id] = isIncluded;
                } else {
                    // If there are construction numbers, filter based on construction number data
                    includedIds[id] = hasIncludedObject(constructionNumbers!);
                }
            });

            filteredData.apartmentBuildings.forEach((apartmentBuilding) => {
                const { id, constructionNumbers, propertyTypes } = apartmentBuilding;

                const hasConstructionNumbers = !!constructionNumbers?.length;
                const hasPropertyTypes = !!propertyTypes?.length;

                if (hasConstructionNumbers) {
                    includedIds[id] = hasIncludedObject(constructionNumbers!);
                } else if (hasPropertyTypes) {
                    includedIds[id] = hasIncludedObject(propertyTypes!);
                } else {
                    includedIds[id] = false;
                }
            });

            filteredData = {
                ...data,
                constructionNumbers: data.constructionNumbers.filter(objectIsIncluded),
                propertyTypes: data.propertyTypes.filter(objectIsIncluded),
                apartmentBuildings: data.apartmentBuildings.filter(objectIsIncluded),
                projectPhases: data.projectPhases.filter((projectPhase) => {
                    return !!inputs.find((input) =>
                        filterFunctions.projectPhase(projectPhase, input),
                    );
                }),
            };
        });
    });

    return filteredData;
}

const getViewTypePolygonOrder = (viewType?: ViewType): HouseSelectorObjectTypeOrder => {
    switch (viewType) {
        case 'propertyType':
        case 'apartmentPropertyType': {
            return PROPERTY_TYPE_VIEW_POLYGON_ORDER;
        }
        default: {
            return CONSTRUCTION_NUMBER_VIEW_POLYGON_ORDER;
        }
    }
};

function sortHouseSelectorData(houseSelectorData: HouseSelectorModel) {
    houseSelectorData.views?.forEach(
        (view) =>
            view.polygons?.length &&
            view.polygons.sort(
                ({ order: orderA = LAST_POLYGON_ORDER }, { order: orderB = LAST_POLYGON_ORDER }) =>
                    orderB - orderA,
            ),
    );
}

const findInPropertiesDataById = (
    id: string,
): { object?: ExtendedApiObject; isSoldProjectData?: boolean } => {
    let object: ExtendedApiObject | undefined;

    const filteredData = getFilteredData();
    if (filteredData) {
        object = findExtendedObjectById(filteredData, id);
        if (object) {
            return { object, isSoldProjectData: propertiesStore.getState().allProjectsSold };
        }
    }

    const soldProjectsData = getSoldProjectsData();
    if (soldProjectsData) {
        object = findExtendedObjectById(soldProjectsData, id);
        if (object) return { object, isSoldProjectData: true };
    }

    return {};
};

const setPolygonDrawingOptions = (polygon: HouseSelectorPolygon, currentView: View) => {
    const { objectId, objectType, linkToViewId } = polygon;
    const { type, slug } = currentView;

    polygon.variant = 'white';
    polygon.canHover = true;
    polygon.hidden = false;
    polygon.order = getViewTypePolygonOrder(type)[objectType];

    if (objectType === 'viewLink' && !!linkToViewId) {
        polygon.variant = 'link';
        return;
    }

    if (!objectId) {
        return;
    }

    const { object, isSoldProjectData } = findInPropertiesDataById(objectId);

    switch (object?.status) {
        case 'InPreparation':
        case 'AmountAvailable':
        case 'SaleSoon': {
            polygon.variant = 'white';
            break;
        }
        case 'UnderConstruction': {
            polygon.variant = 'purple';
            break;
        }
        case 'InOption':
        case 'AssigningOn':
        case 'Reserved': {
            polygon.variant = 'orange';
            break;
        }
        case 'SaleStarted':
        case 'OnSale':
        case 'Available':
        case 'ForRent': {
            polygon.variant = 'green';
            break;
        }
        case 'Sold':
        case 'Rented':
        case 'NotActive': {
            polygon.variant = 'red';
            break;
        }
        default: {
            break;
        }
    }

    const isHighLevelPolygon = ['areaProject', 'district', 'projectPhase'].includes(objectType);

    if (isHighLevelPolygon) {
        polygon.variant = 'white';
        if (!linkToViewId) {
            polygon.canHover = false;
        }
    }

    if (!object) {
        polygon.canHover = false;
        polygon.variant = 'striped';
        polygon.order = LAST_POLYGON_ORDER;
    }

    if (isSoldProjectData) {
        polygon.variant = 'red';
        polygon.order = SOLD_DATA_POLYGON_ORDER[objectType];
    }

    if (slug) {
        const { object: slugObject } = findInPropertiesDataById(slug);
        let polygonCorrelates = false;
        if (!slugObject) return;

        switch (slugObject.objectType) {
            case 'apartmentBuilding': {
                polygonCorrelates =
                    slugObject.id === objectId ||
                    !!slugObject.constructionNumbers?.find((cn) => cn.id === objectId) ||
                    !!slugObject.propertyTypes?.find((cn) => cn.id === objectId);

                break;
            }
            case 'projectPhase': {
                polygonCorrelates =
                    slugObject.id === objectId ||
                    !!slugObject.constructionNumbers?.find((cn) => cn.id === objectId) ||
                    !!slugObject.propertyTypes?.find((cn) => cn.id === objectId);

                break;
            }
            case 'propertyType': {
                polygonCorrelates =
                    slugObject.id === objectId ||
                    !!slugObject.constructionNumbers?.find((cn) => cn.id === objectId);

                break;
            }
        }

        if (isHighLevelPolygon || !polygonCorrelates) {
            polygon.canHover = false;
            polygon.variant = 'striped';
            polygon.order = LAST_POLYGON_ORDER;
        }
    }

    return;
};

const getHouseSelectorHelperData = ({
    objectType,
    objectId,
    linkToViewId,
}: HouseSelectorObject) => {
    let additionalData: Object | undefined;

    if (objectType !== 'viewLink' && objectId) {
        const { object, isSoldProjectData } = findInPropertiesDataById(objectId);
        if (object) {
            const { status } = object;
            additionalData = { status, isSoldProjectData };
        }
    }

    const helperObject = {
        objectId,
        objectType,
        linkToViewId,
        ...(additionalData && additionalData),
    };

    return JSON.stringify(helperObject, null, 2);
};

export {
    filterData,
    filterDataByDistrict,
    filterHouseSelectorData,
    findInPropertiesDataById,
    getHouseSelectorHelperData,
    setPolygonDrawingOptions,
    sortHouseSelectorData,
};
